import React, { ReactNode, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import secureStorage from "../../utils/secureStorage";
import logger from "../../utils/logger";
import authService from '../../services/auth.service';
import systemService from '../../services/system.service';
import { profileSuccess, setBranding } from "../../store/auth/profile/reducer";
import { Ban, AlertTriangle, Clock, AlertCircle, Mail, Phone, Globe } from 'lucide-react';

interface AuthProtectedProps {
  children: ReactNode;
}

// Blocked organization info interface
interface BlockedOrgInfo {
  access_blocked: boolean;
  block_reason?: string;
  block_title?: string;
  block_message?: string;
  contact_message?: string;
  contact_email?: string;
  contact_phone?: string;
  contact_website?: string;
  org_name?: string;
  logo?: string;
}

const AuthProtected: React.FC<AuthProtectedProps> = ({ children }) => {
  const [isValidating, setIsValidating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [blockedOrgInfo, setBlockedOrgInfo] = useState<BlockedOrgInfo | null>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    validateAuth();
  }, []);

  const validateAuth = async () => {
    try {
      // Get auth data from secure storage
      const authUser = secureStorage.getAuthUser();
      const accessToken = secureStorage.getAccessToken();

      logger.info('[AuthProtected] Validating auth', {
        hasAuthUser: !!authUser,
        hasAccessToken: !!accessToken,
        authUserKeys: authUser ? Object.keys(authUser) : [],
        hasExpiresAt: authUser && 'expiresAt' in authUser,
        expiresAt: authUser?.expiresAt
      });

      if (!authUser || !accessToken) {
        logger.warn('[AuthProtected] No auth data found - redirecting to login');
        setIsAuthenticated(false);
        setIsValidating(false);
        return;
      }

      // Check token expiration and validate session
      const isValid = authService.isAuthenticated();

      logger.info('[AuthProtected] Validation result', { isValid });

      if (!isValid) {
        logger.warn('[AuthProtected] Session validation failed - clearing auth data');
        secureStorage.clearAuthData();
        setIsAuthenticated(false);
      } else {
        logger.info('[AuthProtected] Session validated successfully');

        // Check if organization is blocked (suspended/inactive/expired)
        try {
          const brandingResponse = await systemService.getOrganizationBranding();
          if (brandingResponse?.data?.access_blocked) {
            logger.warn('[AuthProtected] Organization is blocked:', brandingResponse.data.block_reason);
            setBlockedOrgInfo({
              access_blocked: true,
              block_reason: brandingResponse.data.block_reason,
              block_title: brandingResponse.data.block_title,
              block_message: brandingResponse.data.block_message,
              contact_message: brandingResponse.data.contact_message,
              contact_email: brandingResponse.data.contact_email,
              contact_phone: brandingResponse.data.contact_phone,
              contact_website: brandingResponse.data.contact_website,
              org_name: brandingResponse.data.org_name,
              logo: brandingResponse.data.logo,
            });
            // Clear auth data since org is blocked
            secureStorage.clearAuthData();
            setIsAuthenticated(false);
            return;
          }
        } catch (brandingError) {
          logger.error('[AuthProtected] Error checking organization status:', brandingError);
        }

        // Restore user data to Redux Profile store on page refresh
        dispatch(profileSuccess(authUser));

        // Restore branding from user.organization if available
        if (authUser?.organization) {
          dispatch(setBranding({
            organizationId: authUser.organization.id,
            org_name: authUser.organization.name,
            logo: authUser.organization.logo,
            favicon: authUser.organization.favicon
          }));
        }

        setIsAuthenticated(true);
      }
    } catch (error) {
      logger.error('[AuthProtected] Auth validation error', error);
      secureStorage.clearAuthData();
      setIsAuthenticated(false);
    } finally {
      setIsValidating(false);
    }
  };

  // Show loading spinner while validating
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          {/* Spinner */}
          <div className="relative inline-block">
            <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-400 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1s' }}></div>
            </div>
          </div>
          {/* Text */}
          <p className="mt-4 text-sm text-slate-600 font-medium">Validating session...</p>
          <p className="mt-1 text-xs text-slate-400">Please wait</p>
        </div>
      </div>
    );
  }

  // Helper function to get block icon based on reason
  const getBlockIcon = (reason?: string) => {
    switch (reason) {
      case 'suspended':
        return <Ban className="w-16 h-16 text-red-500" />;
      case 'inactive':
        return <AlertTriangle className="w-16 h-16 text-orange-500" />;
      case 'trial_expired':
      case 'subscription_expired':
        return <Clock className="w-16 h-16 text-amber-500" />;
      case 'not_found':
      default:
        return <AlertCircle className="w-16 h-16 text-gray-500" />;
    }
  };

  // Helper function to get block color scheme based on reason
  const getBlockColorScheme = (reason?: string) => {
    switch (reason) {
      case 'suspended':
        return {
          bg: 'from-red-50 to-red-100',
          border: 'border-red-200',
          iconBg: 'bg-red-100',
          titleColor: 'text-red-800',
          messageColor: 'text-red-600',
          accentGradient: 'from-red-400 to-red-600',
        };
      case 'inactive':
        return {
          bg: 'from-orange-50 to-orange-100',
          border: 'border-orange-200',
          iconBg: 'bg-orange-100',
          titleColor: 'text-orange-800',
          messageColor: 'text-orange-600',
          accentGradient: 'from-orange-400 to-orange-600',
        };
      case 'trial_expired':
      case 'subscription_expired':
        return {
          bg: 'from-amber-50 to-amber-100',
          border: 'border-amber-200',
          iconBg: 'bg-amber-100',
          titleColor: 'text-amber-800',
          messageColor: 'text-amber-600',
          accentGradient: 'from-amber-400 to-amber-600',
        };
      case 'not_found':
      default:
        return {
          bg: 'from-gray-50 to-gray-100',
          border: 'border-gray-200',
          iconBg: 'bg-gray-100',
          titleColor: 'text-gray-800',
          messageColor: 'text-gray-600',
          accentGradient: 'from-gray-400 to-gray-600',
        };
    }
  };

  // Show blocked organization screen if organization is blocked
  if (blockedOrgInfo?.access_blocked) {
    const colorScheme = getBlockColorScheme(blockedOrgInfo.block_reason);

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 p-4">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.03)_1px,transparent_1px)] bg-[size:64px_64px]"></div>

        <div className={`relative max-w-lg w-full bg-gradient-to-br ${colorScheme.bg} rounded-2xl shadow-xl border ${colorScheme.border} overflow-hidden`}>
          {/* Top accent bar */}
          <div className={`h-2 bg-gradient-to-r ${colorScheme.accentGradient}`}></div>

          <div className="p-8 text-center">
            {/* Organization Logo if available */}
            {blockedOrgInfo.logo && (
              <div className="mb-6">
                <img
                  src={blockedOrgInfo.logo}
                  alt={blockedOrgInfo.org_name || 'Organization'}
                  className="h-12 mx-auto opacity-60"
                />
              </div>
            )}

            {/* Icon */}
            <div className={`inline-flex items-center justify-center w-24 h-24 ${colorScheme.iconBg} rounded-full mb-6`}>
              {getBlockIcon(blockedOrgInfo.block_reason)}
            </div>

            {/* Title */}
            <h1 className={`text-2xl font-bold ${colorScheme.titleColor} mb-3`}>
              {blockedOrgInfo.block_title || 'Access Restricted'}
            </h1>

            {/* Organization Name */}
            {blockedOrgInfo.org_name && (
              <p className="text-lg font-medium text-slate-700 mb-4">
                {blockedOrgInfo.org_name}
              </p>
            )}

            {/* Message */}
            <p className={`${colorScheme.messageColor} mb-6 leading-relaxed`}>
              {blockedOrgInfo.block_message || 'Your organization account is currently unavailable.'}
            </p>

            {/* Contact Section */}
            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 border border-slate-200/60">
              <p className="text-sm font-semibold text-slate-700 mb-4">
                {blockedOrgInfo.contact_message || 'Please contact support for assistance.'}
              </p>

              <div className="space-y-3">
                {/* Email Button */}
                {blockedOrgInfo.contact_email && (
                  <a
                    href={`mailto:${blockedOrgInfo.contact_email}`}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                  >
                    <Mail className="w-4 h-4" />
                    {blockedOrgInfo.contact_email}
                  </a>
                )}

                {/* Phone Button */}
                {blockedOrgInfo.contact_phone && (
                  <a
                    href={`tel:${blockedOrgInfo.contact_phone}`}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors text-sm font-medium"
                  >
                    <Phone className="w-4 h-4" />
                    {blockedOrgInfo.contact_phone}
                  </a>
                )}

                {/* Website Button */}
                {blockedOrgInfo.contact_website && (
                  <a
                    href={blockedOrgInfo.contact_website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors text-sm font-medium"
                  >
                    <Globe className="w-4 h-4" />
                    Visit Website
                  </a>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-slate-200/60">
              <p className="text-xs text-slate-500">
                Powered by{' '}
                <a
                  href="https://basiq360.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-blue-600 hover:text-blue-700"
                >
                  Basiq360
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    logger.info('Redirecting to login - not authenticated');
    return <Navigate to={{ pathname: "/login" }} />;
  }

  // Render protected content
  return <React.Fragment>{children}</React.Fragment>;
};

export default AuthProtected;
